/** Pitch arithmetic: note names, MIDI numbers, Hz, and the pulse wave's Fourier series. Pure. */

const SEMITONE: Readonly<Record<string, number>> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** The lowest and highest notes the score may use: below A0 is mud, above C8 is pain. */
export const MIDI_MIN = 21;
export const MIDI_MAX = 108;

/** "D4", "F#3", "Bb2" -> MIDI note number (A4 = 69, C4 = 60), or null if it is not a note name. */
export function parseNote(name: string): number | null {
  const m = /^([A-G])([#b]?)(-?\d)$/.exec(name);
  if (!m) return null;
  const base = SEMITONE[m[1] ?? ''];
  if (base === undefined) return null;
  const accidental = m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0;
  return (Number(m[3]) + 1) * 12 + base + accidental;
}

/** Pitch class 0-11 of a note letter with an optional accidental ("F#", "Bb"), or null. */
export function pitchClass(name: string): number | null {
  const m = /^([A-G])([#b]?)$/.exec(name);
  if (!m) return null;
  const base = SEMITONE[m[1] ?? ''];
  if (base === undefined) return null;
  const accidental = m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0;
  return (base + accidental + 12) % 12;
}

/** Equal temperament, A4 = 440 Hz. Fractional MIDI numbers are fine (quarter tones, glides). */
export function midiToHz(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export function hzToMidi(hz: number): number {
  return 69 + 12 * Math.log2(hz / 440);
}

/** `hz` moved by `n` semitones. */
export function semitones(hz: number, n: number): number {
  return hz * Math.pow(2, n / 12);
}

/** The lowest MIDI note of pitch class `pc` at or above `floor`: keeps roots in one octave band. */
export function placeAbove(pc: number, floor: number): number {
  return floor + ((((pc - floor) % 12) + 12) % 12);
}

/**
 * Fourier coefficients of a pulse wave with the given duty cycle, for `createPeriodicWave`:
 * the SID chip's signature voice. Index 0 is DC and stays zero.
 */
export function pulseCoefficients(
  duty: number, harmonics: number,
): { real: Float32Array<ArrayBuffer>; imag: Float32Array<ArrayBuffer> } {
  const real = new Float32Array(harmonics + 1);
  const imag = new Float32Array(harmonics + 1);
  for (let n = 1; n <= harmonics; n++) {
    real[n] = (2 * Math.sin(2 * Math.PI * n * duty)) / (Math.PI * n);
    imag[n] = (2 * (1 - Math.cos(2 * Math.PI * n * duty))) / (Math.PI * n);
  }
  return { real, imag };
}
